import React, { useCallback, useRef, useState } from "react";
import { UploadCloud, File as FileIcon, X, CheckCircle2, Loader2 } from "lucide-react";
import { uploadFile } from "@/lib/api";
import { toast } from "sonner";

// Uploads immediately on drop; calls onChange with array of {id, original_filename, category, size}
export function FileDropzone({ category = "Other", multiple = true, value = [], onChange, testId = "file-dropzone", label }) {
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = useCallback(
    async (files) => {
      const list = Array.from(files);
      if (!list.length) return;
      setBusy(true);
      const uploaded = [];
      for (const f of list) {
        try {
          const res = await uploadFile(f, category);
          uploaded.push(res);
        } catch (e) {
          toast.error(`Failed to upload ${f.name}`);
        }
      }
      setBusy(false);
      if (uploaded.length) {
        toast.success(`${uploaded.length} file${uploaded.length > 1 ? "s" : ""} uploaded`);
        onChange(multiple ? [...value, ...uploaded] : uploaded.slice(-1));
      }
    },
    [category, multiple, onChange, value]
  );

  const remove = (id) => onChange(value.filter((v) => v.id !== id));

  return (
    <div data-testid={testId}>
      {label && <div className="text-sm font-semibold text-white/90 mb-2">{label}</div>}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-all ${
          dragging ? "border-gold bg-gold/5" : "border-edge hover:border-gold/50 bg-surface/40"
        }`}
        data-testid={`${testId}-drop`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple={multiple}
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx,.xls,.xlsx,.csv,.txt"
          data-testid={`${testId}-input`}
        />
        {busy ? (
          <Loader2 className="w-7 h-7 mx-auto text-gold animate-spin" />
        ) : (
          <UploadCloud className="w-7 h-7 mx-auto text-gold" />
        )}
        <p className="mt-2 text-sm text-white/80 font-semibold">Drag & drop or click to upload</p>
        <p className="text-xs text-faint mt-1">PDF, images, spreadsheets & documents · up to 25MB each</p>
      </div>

      {value.length > 0 && (
        <ul className="mt-3 space-y-2" data-testid={`${testId}-list`}>
          {value.map((f) => (
            <li key={f.id} className="flex items-center justify-between rounded-lg border border-edge bg-surface/60 px-3 py-2">
              <div className="flex items-center gap-2 min-w-0">
                <CheckCircle2 className="w-4 h-4 text-ok shrink-0" />
                <FileIcon className="w-4 h-4 text-dim shrink-0" />
                <span className="text-sm text-white/85 truncate">{f.original_filename}</span>
              </div>
              <button type="button" onClick={(e) => { e.stopPropagation(); remove(f.id); }} className="text-faint hover:text-brandred p-1" data-testid={`${testId}-remove`}>
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
